import {
  Caption1,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tab,
  TabList,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  Add16Regular,
  ArrowClockwise16Regular,
  Checkmark16Regular,
  ChevronDown12Regular,
  Delete16Regular,
  Edit16Regular,
  Mail16Regular,
  MoreHorizontal16Regular,
  People16Regular,
  PeopleTeam16Regular,
  Person16Regular,
  Shield16Regular,
  PersonAdd16Regular,
  PersonSubtract16Regular,
  Subtract16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListSearch, ListToolbar } from "@/components/list-toolbar";
import { AssignPlanDialog } from "@/features/directory/DirectoryDialogs";
import { InviteMemberDialog } from "@/features/directory/InviteMemberDialog";
import {
  DIRECTORY_ROLE_LABEL,
  DIRECTORY_ROLE_LINE,
  DIRECTORY_SELF_ID,
  DIRECTORY_STATUS_LABEL,
  assignmentTarget,
  teamFormRoster,
  teamMembershipUpdates,
  type DirectoryStatus,
  type TeamRosterPerson,
} from "@/features/directory/directory";
import { removeDirectoryMember, resendDirectoryInvite, setDirectoryRole } from "@/features/directory/directory-store";
import { startAssignedTask, useDirectoryPeople, type DirectoryPerson } from "@/features/directory/directory-view";
import { DirectoryRoleIcon } from "@/features/directory/role-glyphs";
import { WORKSPACE_ACTIONS, WORKSPACE_ROLES, type WorkspaceRole } from "@/features/access/matrix";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { canWorkspace, recordWorkspaceAudit, useWorkspaceRole } from "@/features/access/workspace-access";
import { TeamFormDialog } from "@/features/resources/ResourceDialogs";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { TeamMark } from "@/features/resources/team-appearance";
import { formatAgo } from "@/lib/date-format";
import { contentShell, shellGutter } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";

const ROLE_FILTERS = [
  { value: "all", label: "전체" },
  { value: "owner", label: "소유자" },
  { value: "admin", label: "관리자" },
  { value: "editor", label: "편집자" },
  { value: "viewer", label: "뷰어" },
] as const;

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    boxSizing: "border-box",
  },
  notice: {
    color: tokens.colorNeutralForeground3,
    paddingBottom: "8px",
  },
  frame: { overflow: "auto", minWidth: 0 },
  num: {
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
    whiteSpace: "nowrap",
    "& .fui-TableHeaderCell__button": {
      justifyContent: "center",
    },
  },
  row: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":hover td": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  /** 제목 열. 남는 폭을 흡수하고, 오른쪽에 세로선으로 지표 열과 가른다. */
  nameCol: {
    minWidth: "220px",
    width: "100%",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: "hidden",
    boxSizing: "border-box",
  },
  nameHead: {
    zIndex: 3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  /** 제목 오른쪽 열. 기본 셀(8px)보다 좌우 여백을 줄여 붙인다. */
  metric: {
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },
  memberTable: { minWidth: "840px" },
  roleCol: { width: "112px" },
  teamCol: { width: "136px", overflow: "hidden" },
  projectCol: { width: "80px" },
  taskCol: { width: "64px" },
  activityCol: { width: "104px" },
  statusCol: { width: "80px" },
  actionCol: { width: "44px" },
  teamTable: { minWidth: "648px" },
  descCol: { width: "280px", maxWidth: "280px", overflow: "hidden" },
  facesCol: { width: "104px" },
  personBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    minWidth: 0,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "inherit",
    textAlign: "left",
    cursor: "pointer",
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  name: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  meta: {
    display: "block",
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  quiet: { color: tokens.colorNeutralForeground3 },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "1px 6px",
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
  },
  chipWarn: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  chipDanger: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
  },
  roleLocked: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
  },
  roleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    maxWidth: "100%",
    height: "24px",
    padding: "0 4px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  dangerItem: {
    "&&&": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover:active": { color: tokens.colorPaletteRedForeground1 },
    "&&& .fui-MenuItem__icon": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover .fui-MenuItem__icon": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover:active .fui-MenuItem__icon": { color: tokens.colorPaletteRedForeground1 },
  },
  menuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  roleCopy: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
    maxWidth: "240px",
    whiteSpace: "normal",
  },
  faces: {
    display: "inline-flex",
    alignItems: "center",
  },
  face: {
    position: "relative",
    display: "inline-flex",
    borderRadius: tokens.borderRadiusCircular,
    boxShadow: `0 0 0 2px ${tokens.colorNeutralBackground2}`,
    "& + &": { marginLeft: "-8px" },
  },
  more: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    marginLeft: "-8px",
    borderRadius: tokens.borderRadiusCircular,
    boxShadow: `0 0 0 2px ${tokens.colorNeutralBackground2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  permCell: {
    height: "auto",
    whiteSpace: "normal",
    lineHeight: tokens.lineHeightBase300,
  },
  markHead: { textAlign: "center" },
  markCell: { width: "72px", textAlign: "center" },
  yes: { display: "inline-flex", color: tokens.colorPaletteGreenForeground1 },
  no: { display: "inline-flex", color: tokens.colorNeutralForeground3 },
  maybe: { color: tokens.colorNeutralForeground2 },
});

const ROLE_HEAD: Record<WorkspaceRole, string> = {
  owner: "소유자",
  admin: "관리자",
  editor: "편집자",
  viewer: "뷰어",
  guest: "게스트",
};

type MemberView = "members" | "teams" | "roles";

function MemberStatus({ status }: { status: DirectoryStatus }) {
  const styles = useStyles();
  if (status === "active") return DIRECTORY_STATUS_LABEL.active;
  return (
    <span className={mergeClasses(styles.chip, status === "invited" ? styles.chipWarn : styles.chipDanger)}>
      {DIRECTORY_STATUS_LABEL[status]}
    </span>
  );
}

function MemberFaces({ members }: { members: DirectoryPerson[] }) {
  const styles = useStyles();
  if (members.length === 0) return <span className={styles.quiet}>—</span>;
  const shown = members.slice(0, 3);
  const rest = members.slice(3);
  return (
    <span className={styles.faces} aria-label={`${members.length}명`}>
      {shown.map((person, index) => (
        <span key={person.id} className={styles.face} style={{ zIndex: index + 1 }} title={person.name}>
          <PersonAvatar name={person.name} size={24} tip={false} />
        </span>
      ))}
      {rest.length > 0 ? (
        <span className={styles.more} style={{ zIndex: shown.length + 1 }} title={rest.map((person) => person.name).join(", ")}>
          +{rest.length}
        </span>
      ) : null}
    </span>
  );
}

function memberView(raw: string | null): MemberView {
  if (raw === "teams" || raw === "roles") return raw;
  return "members";
}

function GrantMark({ result }: { result: "allow" | "deny" | "conditional" }) {
  const styles = useStyles();
  if (result === "allow") {
    return (
      <span className={styles.yes} aria-label="허용">
        <Checkmark16Regular />
      </span>
    );
  }
  if (result === "conditional") {
    return (
      <Tooltip content="조건이 맞을 때만 허용됩니다." relationship="label">
        <span className={styles.maybe}>조건</span>
      </Tooltip>
    );
  }
  return (
    <span className={styles.no} aria-label="없음">
      <Subtract16Regular />
    </span>
  );
}

function PermissionTable() {
  const styles = useStyles();
  const table = useDataTableStyles();
  return (
    <div className={mergeClasses(table.frame, styles.frame)}>
      <table className={table.table} aria-label="역할과 권한">
        <thead>
          <tr>
            <th className={table.th}>권한</th>
            {WORKSPACE_ROLES.map((role) => (
              <th key={role} className={mergeClasses(table.th, styles.markHead)}>{ROLE_HEAD[role]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {WORKSPACE_ACTIONS.map((action) => (
            <tr key={action.id}>
              <td className={mergeClasses(table.td, styles.permCell)}>{action.docLabel}</td>
              {WORKSPACE_ROLES.map((role) => (
                <td key={role} className={mergeClasses(table.td, styles.markCell)}>
                  <GrantMark result={action.cells[role].result} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MembersPage() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = memberView(params.get("view"));
  const people = useDirectoryPeople();
  const teams = useCatalogStore((s) => s.teams);
  const resources = useCatalogStore((s) => s.resources);
  const addTeam = useCatalogStore((s) => s.addTeam);
  const updateTeam = useCatalogStore((s) => s.updateTeam);
  const deleteTeam = useCatalogStore((s) => s.deleteTeam);
  const updateResource = useCatalogStore((s) => s.updateResource);
  const role = useWorkspaceRole();
  const canManage = canWorkspace(role, "member.invite");
  const canAssign = role === "owner" || role === "admin" || role === "editor";
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<(typeof ROLE_FILTERS)[number]["value"]>("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [editTeamId, setEditTeamId] = useState<string | null>(null);
  const [deleteTeamId, setDeleteTeamId] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [pick, setPick] = useState<{ name: string; plans: { id: string; name: string }[] } | null>(null);
  usePageAction(null);

  useEffect(() => {
    const invite = params.get("invite") === "1";
    const createTeam = params.get("new") === "team";
    if (!invite && !createTeam) return;
    if (canManage && invite) setInviteOpen(true);
    if (canManage && createTeam) {
      setTeamOpen(true);
      if (params.get("view") !== "teams") {
        const next = new URLSearchParams(params);
        next.set("view", "teams");
        next.delete("invite");
        next.delete("new");
        setParams(next, { replace: true });
        return;
      }
    }
    const next = new URLSearchParams(params);
    next.delete("invite");
    next.delete("new");
    setParams(next, { replace: true });
  }, [params, canManage, setParams]);

  const q = query.trim().toLowerCase();
  const shown = people
    .filter((person) => {
      if (roleFilter !== "all" && person.grant.role !== roleFilter) return false;
      if (!q) return true;
      return person.name.toLowerCase().includes(q) || person.email.toLowerCase().includes(q);
    })
    .sort((a, b) => Number(b.grant.role === "owner") - Number(a.grant.role === "owner"));
  const teamCards = useMemo(() => {
    return Object.values(teams)
      .map((team) => {
        const members = people.filter((person) => person.teamId === team.id);
        return { team, members };
      })
      .filter((card) => !q || card.team.name.toLowerCase().includes(q) || (card.team.description ?? "").toLowerCase().includes(q))
      .sort((a, b) => a.team.name.localeCompare(b.team.name, "ko"));
  }, [teams, people, q]);
  const removing = people.find((person) => person.id === removeId) ?? null;
  const editingTeam = editTeamId ? teams[editTeamId] : null;
  const deletingTeam = deleteTeamId ? teams[deleteTeamId] : null;
  const creatingRoster = teamOpen ? rosterOf(people) : null;
  const editingRoster = editingTeam ? rosterOf(people, editingTeam.id) : null;

  const applyMembers = (teamId: string, memberIds: string[] | undefined, scopeIds: string[]) => {
    if (!memberIds) return;
    for (const patch of teamMembershipUpdates(Object.values(resources), teamId, memberIds, scopeIds)) {
      updateResource(patch.id, { teamId: patch.teamId });
    }
  };

  const removeTeam = (id: string) => {
    const team = teams[id];
    for (const resource of Object.values(resources)) {
      if (resource.teamId === id) updateResource(resource.id, { teamId: undefined });
    }
    deleteTeam(id);
    if (team) recordWorkspaceAudit("팀 삭제", `${team.name} 팀을 삭제했습니다.`);
  };

  const applyRole = (person: DirectoryPerson, next: string | undefined) => {
    if (next !== "admin" && next !== "editor" && next !== "viewer") return;
    if (next === person.grant.role) return;
    if (setDirectoryRole(person.id, next)) {
      recordWorkspaceAudit("역할 변경", `${person.name}의 역할을 ${DIRECTORY_ROLE_LABEL[next]}로 바꿨습니다.`);
    }
  };

  const assign = (person: DirectoryPerson) => {
    const plans = person.work.activePlans.map((plan) => ({ id: plan.id, name: plan.name }));
    const target = assignmentTarget(plans.map((plan) => plan.id), useProjectStore.getState().activeId);
    if (target === "none") return;
    if (target === "pick") {
      setPick({ name: person.name, plans });
      return;
    }
    startAssignedTask(target.planId, person.name, navigate);
  };

  const copyEmail = (email: string) => {
    void navigator.clipboard?.writeText(email);
  };

  const invitedCount = people.filter((person) => person.grant.status === "invited").length;
  const scale = [`${people.length}명`, `팀 ${Object.keys(teams).length}개`, invitedCount > 0 ? `초대 ${invitedCount}건` : ""]
    .filter(Boolean)
    .join(" · ");

  const selectView = (next: MemberView) => {
    if (next === view) return;
    setQuery("");
    const updated = new URLSearchParams(params);
    if (next === "members") updated.delete("view");
    else updated.set("view", next);
    setParams(updated, { replace: true });
  };

  return (
    <div className={styles.page}>
      <ListToolbar
        title="멤버"
        description={scale}
        headingAction={
          view === "teams" && canManage ? (
            <FuiButton appearance="primary" icon={<PeopleTeam16Regular />} onClick={() => setTeamOpen(true)}>
              팀 추가
            </FuiButton>
          ) : view === "members" && canManage ? (
            <FuiButton appearance="primary" icon={<PersonAdd16Regular />} onClick={() => setInviteOpen(true)}>
              멤버 초대
            </FuiButton>
          ) : undefined
        }
        sections={
          <TabList
            size="small"
            selectedValue={view}
            aria-label="멤버 구성"
            onTabSelect={(_, data) => selectView(memberView(typeof data.value === "string" ? data.value : null))}
          >
            <Tab value="members" icon={<People16Regular />}>멤버</Tab>
            <Tab value="teams" icon={<PeopleTeam16Regular />}>팀</Tab>
            <Tab value="roles" icon={<Shield16Regular />}>권한</Tab>
          </TabList>
        }
        search={view === "members" ? <ListSearch value={query} onChange={setQuery} /> : undefined}
        filters={
          view === "members" ? (
            <SegmentedControl
              ariaLabel="역할"
              value={roleFilter}
              options={[...ROLE_FILTERS]}
              onChange={setRoleFilter}
            />
          ) : undefined
        }
      />
      <div className={styles.body}>
        {notice ? <Caption1 className={styles.notice}>{notice}</Caption1> : null}
        {view === "roles" ? (
          <PermissionTable />
        ) : view === "teams" ? (
          teamCards.length === 0 ? (
            <GhostEmptyState
              title={q ? "찾는 팀이 없습니다" : "아직 팀이 없습니다"}
              description={q ? "다른 이름으로 찾아 보세요." : "팀을 만들면 사람을 한 소속으로 묶을 수 있습니다."}
            />
          ) : (
            <Table className={mergeClasses(table.fluent, styles.teamTable)} aria-label="팀">
              <TableHeader>
                <TableRow className={table.headRow}>
                  <TableHeaderCell className={mergeClasses(table.head, styles.nameCol, styles.nameHead)}>팀</TableHeaderCell>
                  <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.descCol)}>설명</TableHeaderCell>
                  <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.facesCol)}>인원</TableHeaderCell>
                  <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.actionCol)} aria-label="동작" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamCards.map((card) => (
                  <TableRow
                    key={card.team.id}
                    className={canManage ? styles.row : undefined}
                    onClick={canManage ? () => setEditTeamId(card.team.id) : undefined}
                  >
                    <TableCell className={styles.nameCol}>
                      {canManage ? (
                        <button type="button" className={styles.personBtn} onClick={() => setEditTeamId(card.team.id)}>
                          <TableCellLayout truncate media={<TeamMark icon={card.team.icon} color={card.team.color} />}>
                            <span className={styles.name} title={card.team.name}>{card.team.name}</span>
                          </TableCellLayout>
                        </button>
                      ) : (
                        <TableCellLayout truncate media={<TeamMark icon={card.team.icon} color={card.team.color} />}>
                          <span className={styles.name} title={card.team.name}>{card.team.name}</span>
                        </TableCellLayout>
                      )}
                    </TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.descCol)}>
                      <span className={mergeClasses(table.clip, card.team.description ? undefined : styles.quiet)} title={card.team.description || undefined}>
                        {card.team.description || "—"}
                      </span>
                    </TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.facesCol)}>
                      <MemberFaces members={card.members} />
                    </TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.actionCol)} onClick={(event) => event.stopPropagation()}>
                      {canManage ? (
                        <Menu>
                          <MenuTrigger disableButtonEnhancement>
                            <button type="button" className={styles.menuBtn} aria-label={`${card.team.name} 동작`}>
                              <MoreHorizontal16Regular />
                            </button>
                          </MenuTrigger>
                          <MenuPopover>
                            <MenuList>
                              <MenuItem icon={<Edit16Regular />} onClick={() => setEditTeamId(card.team.id)}>
                                팀 편집
                              </MenuItem>
                              <MenuDivider />
                              <MenuItem className={styles.dangerItem} icon={<Delete16Regular />} onClick={() => setDeleteTeamId(card.team.id)}>
                                팀 삭제
                              </MenuItem>
                            </MenuList>
                          </MenuPopover>
                        </Menu>
                      ) : (
                        <span className={styles.quiet}>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : shown.length === 0 ? (
          <GhostEmptyState
            title={q || roleFilter !== "all" ? "일치하는 사람이 없습니다" : "아직 멤버가 없습니다"}
            description={q || roleFilter !== "all" ? "검색이나 역할을 바꿔 보세요." : "초대를 보내면 여기에 모입니다."}
          />
        ) : (
          <Table className={mergeClasses(table.fluent, styles.memberTable)} aria-label="멤버">
            <TableHeader>
              <TableRow className={table.headRow}>
                <TableHeaderCell className={mergeClasses(table.head, styles.nameCol, styles.nameHead)}>멤버</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.roleCol)}>역할</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.teamCol)}>팀</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.num, styles.projectCol)}>프로젝트</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.num, styles.taskCol)}>작업</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.activityCol)}>마지막 활동</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.statusCol)}>상태</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.metric, styles.actionCol)} aria-label="동작" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((person) => {
                const locked = person.id === DIRECTORY_SELF_ID || person.grant.role === "owner" || !canManage;
                return (
                  <TableRow key={person.id} className={styles.row} onClick={() => navigate(`/members/${person.id}`)}>
                    <TableCell className={styles.nameCol}>
                      <button type="button" className={styles.personBtn} onClick={() => navigate(`/members/${person.id}`)}>
                        <TableCellLayout
                          truncate
                          media={<PersonAvatar name={person.name} size={28} tip={false} />}
                          description={{ children: person.email, title: person.email }}
                        >
                          <span className={styles.name} title={person.name}>{person.name}</span>
                        </TableCellLayout>
                      </button>
                    </TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.roleCol)} onClick={(event) => event.stopPropagation()}>
                      {locked ? (
                        <span className={person.grant.role === "owner" ? styles.roleLocked : undefined}>
                          {person.grant.role === "owner" ? <DirectoryRoleIcon role="owner" /> : null}
                          <span>
                            {DIRECTORY_ROLE_LABEL[person.grant.role]}
                            {person.grant.kind === "guest" ? <Caption1 className={styles.meta}>게스트</Caption1> : null}
                          </span>
                        </span>
                      ) : (
                        <Menu
                          checkedValues={{ role: [person.grant.role] }}
                          onCheckedValueChange={(_, data) => applyRole(person, data.checkedItems[0])}
                        >
                          <MenuTrigger disableButtonEnhancement>
                            <button type="button" className={styles.roleBtn} aria-label={`${person.name} 역할, ${DIRECTORY_ROLE_LABEL[person.grant.role]}`}>
                              <span className={styles.name}>{DIRECTORY_ROLE_LABEL[person.grant.role]}</span>
                              <ChevronDown12Regular />
                            </button>
                          </MenuTrigger>
                          <MenuPopover>
                            <MenuList>
                              <RoleChoices />
                            </MenuList>
                          </MenuPopover>
                        </Menu>
                      )}
                    </TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.teamCol)}>
                      <span className={mergeClasses(table.clip, person.teamName ? undefined : styles.quiet)} title={person.teamName || undefined}>{person.teamName || "—"}</span>
                    </TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.num, styles.projectCol)}>{person.work.activePlans.length}</TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.num, styles.taskCol)}>{person.work.openTasks.length}</TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.activityCol)}>{person.grant.lastActive ? formatAgo(person.grant.lastActive) : "—"}</TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.statusCol)}>
                      <MemberStatus status={person.grant.status} />
                    </TableCell>
                    <TableCell className={mergeClasses(styles.metric, styles.actionCol)} onClick={(event) => event.stopPropagation()}>
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <button type="button" className={styles.menuBtn} aria-label={`${person.name} 동작`}>
                            <MoreHorizontal16Regular />
                          </button>
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem icon={<Person16Regular />} onClick={() => navigate(`/members/${person.id}`)}>
                              프로필 보기
                            </MenuItem>
                            <MenuItem icon={<Add16Regular />} disabled={!canAssign || person.work.activePlans.length === 0} onClick={() => assign(person)}>
                              작업 배정
                            </MenuItem>
                            <MenuItem icon={<Mail16Regular />} onClick={() => copyEmail(person.email)}>
                              이메일 복사
                            </MenuItem>
                            {!locked && person.grant.status === "invited" ? (
                              <MenuItem
                                icon={<ArrowClockwise16Regular />}
                                onClick={() => {
                                  if (resendDirectoryInvite(person.id)) setNotice(`${person.name} 초대를 다시 보냈습니다.`);
                                }}
                              >
                                초대 다시 보내기
                              </MenuItem>
                            ) : null}
                            {!locked ? (
                              <>
                                <MenuDivider />
                                <MenuItem className={styles.dangerItem} icon={<PersonSubtract16Regular />} onClick={() => setRemoveId(person.id)}>
                                  워크스페이스에서 제거
                                </MenuItem>
                              </>
                            ) : null}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      {inviteOpen ? <InviteMemberDialog open onClose={() => setInviteOpen(false)} /> : null}
      {teamOpen && creatingRoster ? (
        <TeamFormDialog
          open
          people={creatingRoster.choices}
          memberIds={creatingRoster.memberIds}
          onClose={() => setTeamOpen(false)}
          onSave={(team, memberIds) => {
            const id = addTeam(team);
            applyMembers(id, memberIds, creatingRoster.choices.map((person) => person.id));
            setTeamOpen(false);
          }}
        />
      ) : null}
      {editingTeam && editingRoster ? (
        <TeamFormDialog
          open
          initial={editingTeam}
          people={editingRoster.choices}
          memberIds={editingRoster.memberIds}
          onClose={() => setEditTeamId(null)}
          onSave={(next, memberIds) => {
            updateTeam(editingTeam.id, next);
            applyMembers(editingTeam.id, memberIds, editingRoster.choices.map((person) => person.id));
            setEditTeamId(null);
          }}
          onDelete={() => removeTeam(editingTeam.id)}
        />
      ) : null}
      <ConfirmDialog
        open={deletingTeam != null}
        title="팀 삭제"
        message="팀을 삭제할까요? 소속된 사람의 팀은 비워지고, 인력 기록은 남습니다."
        confirmLabel="삭제"
        onCancel={() => setDeleteTeamId(null)}
        onConfirm={() => {
          if (deletingTeam) removeTeam(deletingTeam.id);
          setDeleteTeamId(null);
        }}
      />
      <AssignPlanDialog
        open={pick != null}
        plans={pick?.plans ?? []}
        onClose={() => setPick(null)}
        onPick={(planId) => {
          if (pick) startAssignedTask(planId, pick.name, navigate);
        }}
      />
      <ConfirmDialog
        open={removing != null}
        title="워크스페이스에서 제거"
        message={removing ? "워크스페이스에서 뺄까요? 인력 배정은 플랜에 남습니다." : ""}
        confirmLabel="제거"
        onCancel={() => setRemoveId(null)}
        onConfirm={() => {
          if (removing && removeDirectoryMember(removing.id)) {
            recordWorkspaceAudit("멤버 제거", `${removing.name}을 워크스페이스에서 뺐습니다.`);
          }
          setRemoveId(null);
        }}
      />
    </div>
  );
}

function rosterOf(people: DirectoryPerson[], teamId?: string) {
  return teamFormRoster(
    people.map((person): TeamRosterPerson => ({
      id: person.id,
      name: person.name,
      teamId: person.teamId,
      teamName: person.teamName,
      status: person.grant.status,
      kind: person.grant.kind,
    })),
    teamId,
  );
}

function RoleChoices() {
  const styles = useStyles();
  return (
    <>
      {(["admin", "editor", "viewer"] as const).map((role) => (
        <MenuItemRadio key={role} name="role" value={role} icon={<DirectoryRoleIcon role={role} />}>
          <span className={styles.roleCopy}>
            <span>{DIRECTORY_ROLE_LABEL[role]}</span>
            <Caption1 className={styles.quiet}>{DIRECTORY_ROLE_LINE[role]}</Caption1>
          </span>
        </MenuItemRadio>
      ))}
    </>
  );
}
